<template>
	<ul class="time-off-list-popover-dropdown">
		<li
			v-for="timeOff in timeOffs"
			:key="timeOff.id"
			class="time-off-list-popover-dropdown__row"
		>
			<strong class="time-off-list-popover-dropdown__label">
				<TimeOffEntryLabel :time-off="timeOff" />
			</strong>
			<div class="time-off-list-popover-dropdown__time-split">
				{{ getFormattedTimeSplit(timeOff) }}
			</div>
			<time class="time-off-list-popover-dropdown__period">
				{{ getFormattedPeriod(timeOff) }}
			</time>
			<div
				v-if="showActionButtons && !timeOff.externalSystem"
				class="time-off-list-popover-dropdown__buttons-wrapper"
			>
				<AppDropdown
					:popover-props="{
						distance: 0,
						placement: 'bottom',
						autoHide: false,
					}"
					:trigger-button-props="{
						'v-tooltip':
							isCurrentUserWithoutPermission &&
							timeOff.hasMultipleUsers
								? $t('time-off__no-permission')
								: $t('time-off__modify'),
						'aria-label':
							isCurrentUserWithoutPermission &&
							timeOff.hasMultipleUsers
								? $t('time-off__no-permission')
								: $t('time-off__modify'),
						disabled:
							isCurrentUserWithoutPermission &&
							timeOff.hasMultipleUsers,
					}"
				>
					<template #trigger>
						<SuiIcon
							class="time-off-list-popover-dropdown__modify-button"
							:class="{
								'time-off-list-popover-dropdown__modify-button--disabled':
									isCurrentUserWithoutPermission &&
									timeOff.hasMultipleUsers,
							}"
							name="icon-pencil-16"
						/>
					</template>
					<template #default="{ closeDropdown, isOpen }">
						<TimeOffModifyForm
							:id="timeOff.id"
							:is-open="isOpen"
							@close="closeDropdown"
							@save="$emit('save')"
						/>
					</template>
				</AppDropdown>

				<AppConfirmationPopover
					v-slot="{ openConfirmation }"
					message="time-off__reset-availability-confirmation"
					:message-value="{
						userName: user.name,
						period: `${dateFormat(timeOff.startDate)} -
						${dateFormat(timeOff.endDate)}`,
					}"
					:primary-button-label="
						$t('time-off__reset-availability-confirmation-label')
					"
					:secondary-button-label="
						$t('time-off__reset-availability-cancel-label')
					"
					@confirm="onDelete(timeOff.id, user)"
				>
					<SuiTextButton
						v-tooltip="$t('time-off__delete')"
						class="time-off-list-popover-dropdown__delete-button"
						:aria-label="$t('time-off__delete')"
						@click="openConfirmation"
					>
						<SuiIcon name="icon-bin-16" />
					</SuiTextButton>
				</AppConfirmationPopover>
			</div>
		</li>
	</ul>
</template>

<script setup lang="ts">
import { useFormatter, usePermissions, useTranslation } from '@/composables';
import { type UserTimeOffs } from './types';
import { ALL_DAY_VALUE, SECONDS_IN_A_DAY } from './constants';
import { computed, toRefs } from 'vue';
import { Permission, PermissionAction } from '@/constants/permissions';
import { useCurrentUser } from '@/features/Auth';
import { type TimeOff, useTimeOffDeleteMutation } from '@/generated/graphql';
import { SuiIcon, SuiTextButton } from '@developers/scoro-ui';
import AppConfirmationPopover from '@/features/App/AppConfirmationPopover.vue';
import AppDropdown from '@/features/App/AppDropdown/AppDropdown.vue';
import { useMessage } from '@/features/Message';
import TimeOffEntryLabel from './TimeOffEntryLabel.vue';
import TimeOffModifyForm from './TimeOffModifyForm.vue';
import { TimeOffSystemType } from '@/features/Calendar/components/TimeOff';

export type TimeOffListPopoverDropdownProps = {
	timeOffs: UserTimeOffs['timeOffs'];
	user: UserTimeOffs['user'];
};

const props = defineProps<TimeOffListPopoverDropdownProps>();

const emit = defineEmits(['save']);

const { formatDateNoCurrentYear: dateFormat, formatLongDuration } =
	useFormatter();
const { t } = useTranslation();
const { can } = usePermissions();
const { currentUser } = useCurrentUser();
const { addSuccessMessage } = useMessage();

const { user } = toRefs(props);

const { mutate: deleteTimeOff } = useTimeOffDeleteMutation({});

const canAddAndModifyTimeOff = computed(() =>
	can(PermissionAction.HAS_PERMISSION, Permission.CAN_ADD_AND_MODIFY_TIMEOFF)
);

const canChangeTimeOffForOthers = computed(() =>
	can(
		PermissionAction.HAS_PERMISSION,
		Permission.CAN_CHANGE_TIMEOFF_FOR_OTHER_USERS
	)
);

const currentUserId = computed(() => currentUser.value?.id || '');
const showActionButtons = computed(
	() =>
		canAddAndModifyTimeOff.value &&
		(canChangeTimeOffForOthers.value ||
			currentUserId.value === user.value.id)
);

const isCurrentUserWithoutPermission = computed(
	() =>
		!canChangeTimeOffForOthers.value &&
		currentUserId.value === user.value.id
);

const getFormattedTimeSplit = (timeOff: TimeOff) => {
	if (timeOff.value === ALL_DAY_VALUE) {
		return t('time-off__all-day');
	}

	const prefix =
		timeOff.type.systemName === TimeOffSystemType.ExtraAvailability
			? '+'
			: '-';

	return `${prefix}${formatLongDuration({
		duration: Math.min(timeOff.value, SECONDS_IN_A_DAY),
		removeMinutesOnFullHours: true,
	})}`;
};

const getFormattedPeriod = (timeOff: TimeOff) => {
	const startDate = dateFormat(timeOff.startDate);
	const endDate = dateFormat(timeOff.endDate);

	if (startDate === endDate) {
		return startDate;
	}

	return `${startDate} - ${endDate}`;
};

const onDelete = async (id: TimeOff['id'], user: UserTimeOffs['user']) => {
	const response = await deleteTimeOff({
		input: {
			id,
			userIds: [user.id],
		},
	});

	if (!response?.errors) {
		addSuccessMessage({
			message: t('time-off__save-success-one-user', {
				userName: user.name,
			}),
		});
		emit('save');
	}
};
</script>

<style lang="scss" scoped>
.time-off-list-popover-dropdown {
	margin: 0;
	display: inline-flex;
	padding: 1rem 2rem;
	flex-direction: column;
	align-items: flex-start;
	border-radius: 1rem;
	max-height: calc(50vh - 8rem);

	&__row {
		display: flex;
		gap: 2rem;
		font-size: 1.5rem;
		color: $neutral-90;
		padding: 1.5rem 0.5rem;
		width: 100%;

		&:not(:last-of-type) {
			border-bottom: 1px solid $neutral-20;
		}
	}

	&__label {
		font-weight: $scoro-fw-medium;
		width: 17.5rem;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__time-split {
		width: 9rem;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__period {
		min-width: 11rem;
	}

	&__time-split,
	&__period {
		color: $neutral-90;
		font-feature-settings: 'tnum';
		text-align: right;
	}

	&__buttons-wrapper {
		display: flex;
		gap: 2rem;
		margin-left: auto;
	}

	&__modify-button {
		&:hover:not(&--disabled) {
			--sui-icon-fill: #{$neutral-70};
		}

		&--disabled {
			--sui-icon-fill: #{$neutral-50};
		}
	}

	&__delete-button {
		--sui-color-link: #{$neutral-60};
		--sui-color-link-hover: #{$neutral-70};
	}
}
</style>
